import { useForm, yupResolver } from "@mantine/form";
import { Button, Group, useMantineTheme } from "@mantine/core";
import { useUpdateClientGroupMutation } from "src/generated/graphql";
import TextInput from "src/components/TextInput/TextInput";
import { GetClientGroupMany } from "src/api/graphql/queries/Client";
import { object, string } from "yup";

interface CreateClientGroupProps<T> {
  handleClose: () => void;
  clientId?: string;
  clientGroupId: string;
  clientGroupName: string;
}

type IFormProps = {
  name: string;
};

const clientGroupFormSchema = object().shape({
  name: string().required("Group name is required"),
});

export const EditClientGroupForm = <T,>({
  clientId,
  clientGroupId,
  clientGroupName,
  handleClose,
}: CreateClientGroupProps<T>) => {
  const theme = useMantineTheme();

  const clientGroupInitialValues = {
    name: clientGroupName,
  };

  const form = useForm<IFormProps>({
    initialValues: clientGroupInitialValues,
    validate: yupResolver(clientGroupFormSchema),
  });
  const refetchQuery = [
    { query: GetClientGroupMany, variables: { id: clientId } },
  ];

  const [updateClientGroup, { loading: updateLoading }] =
    useUpdateClientGroupMutation({
      onCompleted: () => {
        handleClose();
      },
      refetchQueries: refetchQuery,
    });

  const handleUpdate = (values: IFormProps) => {
    updateClientGroup({
      variables: {
        id: clientGroupId!,
        data: {
          ...values,
        },
      },
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleUpdate)}>
      <TextInput
        withAsterisk
        label="Group Name"
        {...form.getInputProps("name")}
      />

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={handleClose}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          disabled={updateLoading}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          loading={updateLoading}
        >
          Update
        </Button>
      </Group>
    </form>
  );
};
